/**
 * Azure Neural voice identifiers used by the Weet AI generation pipeline.
 *
 * Canonical values map 1:1 to Azure TTS voice ids.
 *
 * `US_AVIA` / `US_EMMA` / `DE_SERPHINA` / `ZN_XIAO` / `EN_US_JENNY` /
 * `EN_US_BRYAN` / `JP_JP_MASARU` / `ZH_CN_YUNKI` are legacy placeholders
 * kept for backward compatibility. Prefer the canonical members below.
 */
export enum AIVoice {
    // Legacy — kept for backward compat with old speach content
    US_AVIA = "US_AVIA",
    US_EMMA = "US_EMMA",
    DE_SERPHINA = "DE_SERPHINA",
    ZN_XIAO = "ZN_XIAO",
    EN_US_JENNY = "EN_US_JENNY",
    EN_US_BRYAN = "EN_US_BRYAN",
    JP_JP_MASARU = "JP_JP_MASARU",
    ZH_CN_YUNKI = "ZH_CN_YUNKI",

    // Canonical Azure Neural voice ids
    EN_US_ADAM = "en-US-AdamMultilingualNeural",
    EN_US_AMANDA = "en-US-AmandaMultilingualNeural",
    EN_US_ANDREW = "en-US-AndrewMultilingualNeural",
    EN_US_AVA = "en-US-AvaMultilingualNeural",
    EN_US_BRANDON = "en-US-BrandonMultilingualNeural",
    EN_US_BRIAN = "en-US-BrianMultilingualNeural",
    EN_US_CHRISTOPHER = "en-US-ChristopherMultilingualNeural",
    EN_US_CORA = "en-US-CoraMultilingualNeural",
    EN_US_DAVIS = "en-US-DavisMultilingualNeural",
    EN_US_DEREK = "en-US-DerekMultilingualNeural",
    EN_US_DUSTIN = "en-US-DustinMultilingualNeural",
    EN_US_EMMA = "en-US-EmmaMultilingualNeural",
    EN_US_EVELYN = "en-US-EvelynMultilingualNeural",
    EN_US_LEWIS = "en-US-LewisMultilingualNeural",
    EN_US_LOLA = "en-US-LolaMultilingualNeural",
    EN_US_NANCY = "en-US-NancyMultilingualNeural",
    EN_US_PHOEBE = "en-US-PhoebeMultilingualNeural",
    EN_US_SAMUEL = "en-US-SamuelMultilingualNeural",
    EN_US_SERENA = "en-US-SerenaMultilingualNeural",
    EN_US_STEFFAN = "en-US-SteffanMultilingualNeural",
    EN_US_ALLOY_TURBO = "en-US-AlloyTurboMultilingualNeural",
    EN_US_ECHO_TURBO = "en-US-EchoTurboMultilingualNeural",
    EN_US_FABLE_TURBO = "en-US-FableTurboMultilingualNeural",
    EN_US_NOVA_TURBO = "en-US-NovaTurboMultilingualNeural",
    EN_US_ONYX_TURBO = "en-US-OnyxTurboMultilingualNeural",
    EN_US_SHIMMER_TURBO = "en-US-ShimmerTurboMultilingualNeural",
    EN_GB_ADA = "en-GB-AdaMultilingualNeural",
    EN_GB_OLLIE = "en-GB-OllieMultilingualNeural",
    DE_DE_SERAPHINA = "de-DE-SeraphinaMultilingualNeural",
    DE_DE_FLORIAN = "de-DE-FlorianMultilingualNeural",
    ES_ES_ARABELLA = "es-ES-ArabellaMultilingualNeural",
    ES_ES_ISIDORA = "es-ES-IsidoraMultilingualNeural",
    ES_ES_TRISTAN = "es-ES-TristanMultilingualNeural",
    ES_ES_XIMENA = "es-ES-XimenaMultilingualNeural",
    FR_FR_LUCIEN = "fr-FR-LucienMultilingualNeural",
    FR_FR_VIVIENNE = "fr-FR-VivienneMultilingualNeural",
    FR_FR_REMY = "fr-FR-RemyMultilingualNeural",
    IT_IT_ALESSIO = "it-IT-AlessioMultilingualNeural",
    IT_IT_GIUSEPPE = "it-IT-GiuseppeMultilingualNeural",
    IT_IT_ISABELLA = "it-IT-IsabellaMultilingualNeural",
    IT_IT_MARCELLO = "it-IT-MarcelloMultilingualNeural",
    JA_JP_MASARU = "ja-JP-MasaruMultilingualNeural",
    KO_KR_HYUNSU = "ko-KR-HyunsuMultilingualNeural",
    PT_BR_MACERIO = "pt-BR-MacerioMultilingualNeural",
    PT_BR_THALITA = "pt-BR-ThalitaMultilingualNeural",
    ZH_CN_XIAOXIAO = "zh-CN-XiaoxiaoMultilingualNeural",
    ZH_CN_XIAOCHEN = "zh-CN-XiaochenMultilingualNeural",
    ZH_CN_XIAOYU = "zh-CN-XiaoyuMultilingualNeural",
    ZH_CN_YUNYI = "zh-CN-YunyiMultilingualNeural",
    ZH_CN_YUNFAN = "zh-CN-YunfanMultilingualNeural",
    ZH_CN_YUNXIAO = "zh-CN-YunxiaoMultilingualNeural",

    // Gemini voices (advanced TTS — shown only when ENABLE_ADVANCED_TTS is on).
    // Ids carry no locale; the value IS the Gemini voice name.
    GEMINI_ZEPHYR = "Zephyr",
    GEMINI_PUCK = "Puck",
    GEMINI_CHARON = "Charon",
    GEMINI_KORE = "Kore",
    GEMINI_FENRIR = "Fenrir",
    GEMINI_LEDA = "Leda",
    GEMINI_ORUS = "Orus",
    GEMINI_AOEDE = "Aoede",
    GEMINI_CALLIRRHOE = "Callirrhoe",
    GEMINI_AUTONOE = "Autonoe",
    GEMINI_ENCELADUS = "Enceladus",
    GEMINI_IAPETUS = "Iapetus",
    GEMINI_UMBRIEL = "Umbriel",
    GEMINI_ALGIEBA = "Algieba",
    GEMINI_DESPINA = "Despina",
    GEMINI_ERINOME = "Erinome",
    GEMINI_ALGENIB = "Algenib",
    GEMINI_RASALGETHI = "Rasalgethi",
    GEMINI_LAOMEDEIA = "Laomedeia",
    GEMINI_ACHERNAR = "Achernar",
    GEMINI_ALNILAM = "Alnilam",
    GEMINI_SCHEDAR = "Schedar",
    GEMINI_GACRUX = "Gacrux",
    GEMINI_PULCHERRIMA = "Pulcherrima",
    GEMINI_ACHIRD = "Achird",
    GEMINI_ZUBENELGENUBI = "Zubenelgenubi",
    GEMINI_VINDEMIATRIX = "Vindemiatrix",
    GEMINI_SADACHBIA = "Sadachbia",
    GEMINI_SADALTAGER = "Sadaltager",
    GEMINI_SULAFAT = "Sulafat"
}
